import { useState } from 'react';

interface Props {
  /** Sepolia (or mainnet, see network prop) transaction hash. */
  hash: string;
  /** Short label, e.g. "contribute(0)" or "submitProposal(...)". */
  action: string;
  /** Optional one-line context: who did this and why. */
  caller?: string;
  /** Optional condensed argv summary, free-form. */
  args?: string;
  /** Defaults to "sepolia". */
  network?: 'sepolia' | 'mainnet';
}

/**
 * Inline reference to a real on-chain transaction. Renders a clickable chip
 * (action label + short hash → Etherscan) and, when `caller` or `args` are
 * provided, a sibling "details" button that toggles a small panel below the
 * chip with the full context. Author-time vetting of which tx to feature is
 * a deliberate part of the docs workflow; the widget does no on-chain fetch.
 */
export default function TxExample({
  hash,
  action,
  caller,
  args,
  network = 'sepolia',
}: Props) {
  const [open, setOpen] = useState(false);
  const short = hash.slice(0, 10) + '…' + hash.slice(-8);
  const explorer =
    network === 'mainnet'
      ? 'https://etherscan.io/tx/' + hash
      : 'https://sepolia.etherscan.io/tx/' + hash;
  const hasDetail = Boolean(caller || args);

  return (
    <span className={'tx-example' + (open ? ' is-open' : '')}>
      <span className="tx-example-row">
        <a
          href={explorer}
          target="_blank"
          rel="noreferrer"
          className="tx-example-link"
          title={'Open ' + short + ' on Etherscan'}
        >
          <code>{action}</code>
          <span className="tx-example-hash">{short}</span>
          <span className="tx-example-ext" aria-hidden="true">↗</span>
        </a>
        {hasDetail && (
          <button
            type="button"
            className="tx-example-toggle"
            aria-expanded={open}
            aria-controls={'tx-detail-' + short}
            title={open ? 'Hide caller and args' : 'Show caller and args'}
            onClick={() => setOpen((v) => !v)}
          >
            <span>{open ? 'hide details' : 'show details'}</span>
            <span className="tx-example-chevron" aria-hidden="true">
              {open ? '▴' : '▾'}
            </span>
          </button>
        )}
      </span>
      {open && hasDetail && (
        <span id={'tx-detail-' + short} className="tx-example-detail">
          {caller && (
            <span className="tx-example-detail-row">
              <b>From</b>
              <code>{caller}</code>
            </span>
          )}
          {args && (
            <span className="tx-example-detail-row">
              <b>Args</b>
              <span>{args}</span>
            </span>
          )}
          <span className="tx-example-detail-row">
            <b>Hash</b>
            <code>{hash}</code>
          </span>
        </span>
      )}
      <style>{css}</style>
    </span>
  );
}

const css = `
.tx-example {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin: 2px 2px;
  vertical-align: middle;
}
.tx-example-row {
  display: inline-flex;
  align-items: stretch;
  gap: 4px;
}
.tx-example-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  background: var(--c-bg-alt);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  font-size: 0.85em;
  text-decoration: none;
  line-height: 1.5;
}
.tx-example-link:hover {
  border-color: var(--c-accent);
  text-decoration: none;
}
.tx-example-link code {
  background: transparent;
  padding: 0;
  font-size: 1em;
}
.tx-example-hash {
  font-family: var(--font-mono);
  color: var(--c-muted);
  font-size: 0.92em;
}
.tx-example-ext {
  font-size: 0.85em;
  color: var(--c-muted);
}
.tx-example-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: var(--c-bg-alt);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  font-size: 0.78em;
  font-family: var(--font-sans, inherit);
  line-height: 1.5;
  color: var(--c-muted);
  cursor: pointer;
  text-transform: lowercase;
  letter-spacing: 0.02em;
}
.tx-example-toggle:hover {
  color: var(--c-fg);
  border-color: var(--c-accent);
}
.tx-example.is-open .tx-example-toggle {
  color: var(--c-fg);
  border-color: var(--c-accent);
  background: var(--c-bg);
}
.tx-example-chevron {
  font-size: 0.85em;
  line-height: 1;
}
.tx-example-detail {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: var(--c-bg-alt);
  border: 1px solid var(--c-border);
  border-left: 3px solid var(--c-accent);
  border-radius: var(--radius-sm);
  font-size: 0.82em;
  max-width: 100%;
}
.tx-example-detail-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.tx-example-detail b {
  display: inline-block;
  min-width: 44px;
  font-size: 0.85em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--c-muted);
}
.tx-example-detail code {
  background: transparent;
  padding: 0;
  font-family: var(--font-mono);
  font-size: 0.95em;
  word-break: break-all;
}
`;
